import { ChangeEvent, ReactElement } from 'react';
import { tokens } from '../../theme';

export type FormFieldState = 'default' | 'valid' | 'error';

const LABEL_GAP = 4;
const INPUT_HEIGHT = 44;
// Fixo nos dois breakpoints no Figma (mobile e desktop usam p-[12px]) — não usar tokens.spacing.small,
// que é fluido (8–12) e renderizaria 8px no mobile em vez dos 12px do design.
const INPUT_PADDING = 12;

/** Classe utilitária definida em src/index.css — cor do placeholder não é alcançável via style inline. */
const INPUT_CLASS_NAME = 'field-input';

const BORDER_COLOR_BY_STATE: Record<FormFieldState, string> = {
  default: tokens.colors.border.field,
  valid: tokens.colors.brand.green.DEFAULT,
  error: tokens.colors.brand.red.DEFAULT,
};

type FormFieldProps = {
  id: string;
  label: string;
  value: string;
  placeholder: string;
  state: FormFieldState;
  onChange: (value: string) => void;
  onBlur: () => void;
  disclaimer?: string;
  error?: string;
  optional?: boolean;
  inputMode?: 'text' | 'numeric' | 'email' | 'tel';
};

export const FormField = ({
  id,
  label,
  value,
  placeholder,
  state,
  onChange,
  onBlur,
  disclaimer,
  error,
  optional = false,
  inputMode = 'text',
}: FormFieldProps): ReactElement => {
  const helperText = state === 'error' ? error : disclaimer;
  const helperId = helperText ? `${id}-helper` : undefined;

  return (
    <div style={styles.field}>
      <div style={styles.labelRow}>
        <label htmlFor={id} style={styles.label}>
          {label}
        </label>
        {optional && <span style={styles.optionalTag}>Opcional</span>}
      </div>
      <input
        id={id}
        className={INPUT_CLASS_NAME}
        value={value}
        placeholder={placeholder}
        inputMode={inputMode}
        aria-invalid={state === 'error'}
        aria-describedby={helperId}
        onChange={(event: ChangeEvent<HTMLInputElement>) => onChange(event.target.value)}
        onBlur={onBlur}
        style={
          {
            ...styles.input,
            borderColor: BORDER_COLOR_BY_STATE[state],
            '--placeholder-color': tokens.colors.brand.gray.light650,
          } as React.CSSProperties
        }
      />
      {helperText && (
        <p
          id={helperId}
          role={state === 'error' ? 'alert' : undefined}
          style={{
            ...styles.helper,
            color:
              state === 'error'
                ? tokens.colors.brand.red.DEFAULT
                : tokens.colors.brand.gray.DEFAULT,
          }}
        >
          {helperText}
        </p>
      )}
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: LABEL_GAP,
    width: '100%',
    boxSizing: 'border-box',
  },
  labelRow: {
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
    gap: tokens.spacing.small,
  },
  label: {
    fontSize: tokens.typography.fontSize.caption,
    fontWeight: tokens.typography.fontWeight.regular,
    color: tokens.colors.brand.gray.dark150,
  },
  optionalTag: {
    fontSize: tokens.typography.fontSize.disclaimer,
    color: tokens.colors.brand.gray.DEFAULT,
  },
  input: {
    height: INPUT_HEIGHT,
    width: '100%',
    boxSizing: 'border-box',
    padding: INPUT_PADDING,
    borderRadius: tokens.radius.small,
    border: '1px solid',
    backgroundColor: tokens.colors.global.white,
    fontSize: tokens.typography.fontSize.input,
    fontFamily: 'inherit',
    color: tokens.colors.global.black,
  },
  helper: {
    margin: 0,
    fontSize: tokens.typography.fontSize.disclaimer,
    textAlign: 'left',
  },
};
